"use dom";

import TooltipWrapper from "@memorang/ui/components/TooltipWrapper";
import { useAuthTokenGetter } from "@memorang/ui/contexts/AuthTokenContext";
import {
	claimAudioFocus,
	releaseAudioFocus,
} from "@memorang/ui/helpers/audio-focus";
import type { IconType } from "@memorang/ui/icon";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Animated } from "react-native";
import { IconButton } from "react-native-paper";
import { useAppTheme } from "../useAppTheme";
import { useTTSContext } from "./TTSContext";
import { TTS_PLAY_ICON } from "./constants";
import type { TTSPlayerRef } from "./types";
import { useTTSAnimation } from "./useTTSAnimation";
import { getTTSCacheKey, getTtsAudioData } from "./utils";

type Props = {
	itemId: string;
	explanation?: string;
	playIcon?: IconType;
	mode?: React.ComponentProps<typeof IconButton>["mode"];
	size?: number;
	disabled?: boolean;
	allowPrefetch?: boolean;
	eventName?: string;
	trackCustomEvent?: (
		params: Record<string, string | number | undefined>,
	) => void;
	iconColor?: string;
	playOnce?: boolean;
	onEnded?: () => void;
};

type PlaybackStatus = "stopped" | "playing" | "paused" | "loading" | "unloaded";

const TTSContainer = forwardRef<TTSPlayerRef, Props>(function TTSContainer(
	{
		explanation: initialExplanation,
		playIcon = TTS_PLAY_ICON,
		mode,
		size = 24,
		disabled = false,
		allowPrefetch: _allowPrefetch = false, // not required as web & android support true streaming
		itemId,
		trackCustomEvent,
		eventName,
		iconColor,
		playOnce = false,
		onEnded,
	}: Props,
	ref,
) {
	const [playbackStatus, setPlaybackStatus] =
		useState<PlaybackStatus>("stopped");
	const [isPlaying, setIsPlaying] = useState(false);
	const [hasEnded, setHasEnded] = useState(false);
	const hasEndedRef = useRef(false);
	const onEndedRef = useRef(onEnded);
	useEffect(() => {
		onEndedRef.current = onEnded;
	}, [onEnded]);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const mediaSourceRef = useRef<MediaSource | null>(null);
	const theme = useAppTheme();
	const { tenantId, org, appId, appName } = useTTSContext();
	const getAuthToken = useAuthTokenGetter();
	const animatedStyle = useTTSAnimation(isPlaying);
	const { t } = useTranslation();

	// Invalidate streaming if playback is paused or audio focus is lost before it starts.
	const playCancelledRef = useRef(false);

	const pausePlayback = useCallback(() => {
		playCancelledRef.current = true;
		audioRef.current?.pause();
	}, []);

	const markEnded = useCallback(() => {
		if (hasEndedRef.current) {
			return;
		}
		hasEndedRef.current = true;
		setHasEnded(true);
		onEndedRef.current?.();
	}, []);

	const handleError = useCallback((message: string) => {
		console.error(message);
		if (audioRef.current) {
			const currentSrc = audioRef.current.src;
			audioRef.current.pause();
			audioRef.current.src = "";
			if (mediaSourceRef.current && currentSrc) {
				URL.revokeObjectURL(currentSrc);
			}
		}
	}, []);

	const handleTracking = useCallback(
		(status: PlaybackStatus) => {
			if (trackCustomEvent && eventName) {
				trackCustomEvent({
					eventName,
					itemId,
					status,
				});
			}
		},
		[trackCustomEvent, eventName, itemId],
	);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	const startStreaming = useCallback(async () => {
		if (!initialExplanation?.trim()) {
			handleError("Text cannot be empty");
			return;
		}

		playCancelledRef.current = false;
		claimAudioFocus(pausePlayback);

		if (audioRef?.current?.src) {
			audioRef.current.play();
			setPlaybackStatus("playing");
			handleTracking("playing");
			return;
		}

		setPlaybackStatus("loading");

		try {
			const audio = new Audio();
			audioRef.current = audio;

			audio.addEventListener("play", () => {
				setIsPlaying(true);
				setPlaybackStatus("playing");
				handleTracking("playing");
			});

			audio.addEventListener("pause", () => {
				setIsPlaying(false);
				setPlaybackStatus("paused");
				handleTracking("paused");
			});

			audio.addEventListener("ended", () => {
				setIsPlaying(false);
				setPlaybackStatus("stopped");
				handleTracking("stopped");
				markEnded();
			});

			const ttsKey = await getTTSCacheKey(
				org ?? "",
				tenantId ?? "",
				appId ?? "",
				initialExplanation,
			);

			const token = await getAuthToken();
			const audioData = await getTtsAudioData(
				initialExplanation,
				appName ?? "",
				ttsKey,
				token,
				true,
			);

			if (playCancelledRef.current) {
				setPlaybackStatus("stopped");
				return;
			}

			// If we got a cached URL, use it directly
			if (typeof audioData === "string") {
				audio.src = audioData;
				await audio.play();
				setPlaybackStatus("playing");
				handleTracking("playing");
				return;
			}

			// Handle streaming response
			const mediaSource = new MediaSource();
			mediaSourceRef.current = mediaSource;

			audio.src = URL.createObjectURL(mediaSource);

			// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
			mediaSource.addEventListener("sourceopen", async () => {
				try {
					const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
					const reader = audioData.getReader();
					const bufferQueue: Uint8Array[] = [];
					let isProcessing = false;

					// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
					const processQueue = async () => {
						if (
							isProcessing ||
							bufferQueue.length === 0 ||
							sourceBuffer.updating
						) {
							return;
						}

						isProcessing = true;
						try {
							const chunk = bufferQueue.shift();
							if (chunk) {
								const arrayBuffer = new ArrayBuffer(chunk.byteLength);
								const uint8View = new Uint8Array(arrayBuffer);
								uint8View.set(chunk);
								sourceBuffer.appendBuffer(arrayBuffer);
								await new Promise<void>((resolve) => {
									sourceBuffer.addEventListener(
										"updateend",
										() => {
											isProcessing = false;
											resolve();
											processQueue();
										},
										{ once: true },
									);
								});
							}
						} catch (err) {
							isProcessing = false;
							handleError(
								`Buffer error: ${err instanceof Error ? err.message : String(err)}`,
							);
						}
					};

					while (true) {
						const { done, value } = await reader.read();
						if (done) {
							break;
						}

						bufferQueue.push(value);
						if (!isProcessing) {
							await processQueue();
						}
					}

					while (bufferQueue.length > 0 || sourceBuffer.updating) {
						await new Promise((resolve) => setTimeout(resolve, 100));
					}
					mediaSource.endOfStream();
				} catch (err) {
					handleError(
						`Failed to process audio stream: ${err instanceof Error ? err.message : String(err)}`,
					);
				}
			});

			if (playCancelledRef.current) {
				setPlaybackStatus("stopped");
				return;
			}

			await audio.play();
			setPlaybackStatus("playing");
		} catch (err) {
			handleError(
				`Failed to generate audio: ${err instanceof Error ? err.message : "Unknown error"}`,
			);
			throw err;
		}
	}, [
		initialExplanation,
		handleError,
		org,
		tenantId,
		appId,
		appName,
		handleTracking,
		getAuthToken,
		pausePlayback,
		markEnded,
	]);

	const togglePlayback = useCallback(() => {
		if (!audioRef.current) {
			return;
		}
		if (isPlaying) {
			audioRef.current.pause();
			setPlaybackStatus("paused");
			if (playOnce) {
				markEnded();
			}
		} else {
			playCancelledRef.current = false;
			claimAudioFocus(pausePlayback);
			audioRef.current.play().catch((err) => {
				handleError(`Playback failed: ${err.message}`);
			});
			setPlaybackStatus("playing");
		}
	}, [isPlaying, handleError, pausePlayback, playOnce, markEnded]);

	const playForSequence = useCallback(async () => {
		if (hasEndedRef.current) {
			return;
		}
		if (audioRef.current?.src) {
			playCancelledRef.current = false;
			claimAudioFocus(pausePlayback);
			await audioRef.current.play();
			setPlaybackStatus("playing");
			return;
		}
		await startStreaming();
	}, [startStreaming, pausePlayback]);

	useImperativeHandle(
		ref,
		() => ({
			play: playForSequence,
			pause: pausePlayback,
			isEnded: hasEnded,
			isPlaying,
			isLoaded: true,
		}),
		[playForSequence, pausePlayback, hasEnded, isPlaying],
	);

	useEffect(() => {
		return () => {
			releaseAudioFocus(pausePlayback);
			if (audioRef.current) {
				const currentSrc = audioRef.current.src;
				audioRef.current.pause();
				audioRef.current.src = "";
				if (mediaSourceRef.current && currentSrc) {
					URL.revokeObjectURL(currentSrc);
				}
			}
		};
	}, [pausePlayback]);

	const isPlayedOut = playOnce && hasEnded;

	const handlePlaybackControl = useCallback(() => {
		if (playbackStatus === "loading" || (playOnce && hasEndedRef.current)) {
			return;
		}
		if (playbackStatus === "stopped") {
			startStreaming().catch(() => undefined);
		} else {
			togglePlayback();
		}
	}, [playbackStatus, startStreaming, togglePlayback, playOnce]);

	const getPlaybackIcon = () => {
		if (isPlayedOut) {
			return "check-circle";
		}
		switch (playbackStatus) {
			case "stopped":
			case "unloaded":
			case "paused":
				return playIcon;
			case "playing":
				return "pause";
			default:
				return "play";
		}
	};

	const circleSize = size + 8;

	return (
		<TooltipWrapper title={t("tts.tooltip")}>
			<Animated.View
				style={{
					position: "relative",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Animated.View
					style={[
						{
							position: "absolute",
							width: circleSize,
							height: circleSize,
							borderRadius: circleSize / 2,
							borderWidth: 1.5,
							borderColor: iconColor || theme.colors.primary,
						},
						animatedStyle,
					]}
				/>
				<TooltipWrapper title={t("tts.tooltip")}>
					<IconButton
						icon={getPlaybackIcon()}
						size={size}
						mode={mode}
						onPress={handlePlaybackControl}
						disabled={playbackStatus === "loading" || disabled || isPlayedOut}
						loading={playbackStatus === "loading"}
						style={{ margin: 0 }}
						iconColor={iconColor}
					/>
				</TooltipWrapper>
			</Animated.View>
		</TooltipWrapper>
	);
});

export default TTSContainer;
